import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
// Remove the incorrect authService import
// import { authService } from 'services';
const LoginForm = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      console.log('[LoginForm] Form validation failed');
      return;
    }

    console.log('[LoginForm] Login attempt started', { 
      email: formData.email,
      hasPassword: !!formData.password 
    });
    
    setIsLoading(true);

    try {
      console.log('[LoginForm] Calling login from useAuth');
      // Use the login function from useAuth hook
      const result = await login({
        email: formData.email,
        password: formData.password
      });
      
      console.log('[LoginForm] Login response received', { 
        hasResult: !!result,
        hasUser: !!result?.user,
        hasToken: !!result?.token,
        userRole: result?.user?.role 
      });
      
      if (result && result.user) {
        // User data is already stored by the AuthContext
        console.log('[LoginForm] User data stored by AuthContext');

        if (formData?.rememberMe) {
          localStorage.setItem('rememberMe', 'true');
          console.log('[LoginForm] Remember me preference saved');
        }

        console.log('[LoginForm] Redirecting based on user role:', result.user.role);
        
        // Redirect based on user role
        switch (result.user.role) {
          case 'hr_manager':
            navigate('/hr-roles/manager');
            break;
          case 'recruiter':
            navigate('/hr-roles/recruiter');
            break;
          case 'hr_coordinator':
            navigate('/hr-roles/coordinator');
            break;
          case 'admin':
            navigate('/hr-dashboard'); // Admin goes to main dashboard
            break;
          default:
            navigate('/hr-dashboard'); // Fallback to main dashboard
            break;
        }
      } else {
        console.error('[LoginForm] Login failed - no user data in response', result);
        setErrors({
          general: 'Login failed. Please check your credentials.'
        });
      }
    } catch (error) {
      console.error('[LoginForm] Login error caught', { 
        errorMessage: error.message,
        errorStack: error.stack,
        errorResponse: error.response?.data 
      });
      
      // Check if it's a network error (backend not running)
      if (error.message.includes('Network Error') || 
          error.message.includes('ECONNREFUSED') ||
          error.message.includes('ERR_NETWORK')) {
        console.error('[LoginForm] Network error detected - backend may be down');
        setErrors({
          general: 'Cannot connect to server. Please ensure the backend is running on port 8080. Default credentials: admin@hraisystem.com / admin123'
        });
      } else if (error.message.includes('401') || 
                 error.message.includes('Unauthorized') ||
                 error.message.includes('Invalid credentials')) {
        console.error('[LoginForm] Authentication failed - invalid credentials');
        setErrors({
          general: 'Invalid credentials. Default credentials: admin@hraisystem.com / admin123'
        });
      } else if (error.message.includes('500')) {
        console.error('[LoginForm] Server error detected');
        setErrors({
          general: 'Server error occurred. Please try again or contact support.'
        });
      } else {
        console.error('[LoginForm] Unknown error');
        setErrors({
          general: `Login failed: ${error.message}. Default credentials: admin@hraisystem.com / admin123`
        });
      }
    } finally {
      console.log('[LoginForm] Login attempt completed');
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    alert('Password reset functionality will be implemented. For testing, use: admin@hraisystem.com / admin123');
  };

  const handleRequestAccess = () => {
    alert('Access request functionality will be implemented. For testing, use: admin@hraisystem.com / admin123');
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* General Error Message */}
        {errors?.general && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
              <p className="text-sm text-destructive font-medium">Authentication Failed</p>
            </div>
            <p className="text-sm text-destructive mt-1">{errors?.general}</p>
          </div>
        )}

        {/* Email Field */}
        <Input
          label="Email Address"
          type="email"
          name="email"
          placeholder="Enter your work email"
          value={formData?.email}
          onChange={handleInputChange}
          error={errors?.email}
          required
          disabled={isLoading}
        />

        {/* Password Field */}
        <Input
          label="Password"
          type="password"
          name="password"
          placeholder="Enter your password"
          value={formData?.password}
          onChange={handleInputChange}
          error={errors?.password}
          required
          disabled={isLoading}
        />

        {/* Remember Me Checkbox */}
        <div className="flex items-center justify-between">
          <Checkbox
            label="Remember me"
            name="rememberMe"
            checked={formData?.rememberMe}
            onChange={handleInputChange}
            disabled={isLoading}
          />
          
          <button
            type="button"
            onClick={handleForgotPassword}
            className="text-sm text-primary hover:text-primary/80 transition-colors"
            disabled={isLoading}
          >
            Forgot password?
          </button>
        </div>

        {/* Sign In Button */}
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          iconName="LogIn"
          iconPosition="right"
          disabled={isLoading}
        >
          {isLoading ? 'Signing In...' : 'Sign In'}
        </Button>

        {/* Request Access Link */}
        <div className="text-center">
          <button
            type="button"
            onClick={handleRequestAccess}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            disabled={isLoading}
          >
            Need access? Request an account
          </button>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;